'use client'

import {
    AnamnesisTemplate,
    createAnamnesis,
    CreateAnamnesisData,
    getAnamnesis,
    getAnamnesisTemplates,
    MEDICAL_SPECIALTY_LABELS,
    updateAnamnesis,
    UpdateAnamnesisData
} from '@/lib/api/prontuario/prontuario-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdAssignment,
    MdSave,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioNavigate, useProntuarioParams } from './prontuario-context'

interface FormData {
    professionalId: string
    templateId: number | null
    consultationId: number | null
    occupation: string
    religion: string
    informant: string
    reliability: string
    chiefComplaint: string
    historyOfPresentIllness: string
    systemsReview: any
    lifeHabits: any
    socialConditions: any
    physicalExam: any
    diagnosticHypothesis: string
}

const defaultFormData: FormData = {
    professionalId: '',
    templateId: null,
    consultationId: null,
    occupation: '',
    religion: '',
    informant: '',
    reliability: '',
    chiefComplaint: '',
    historyOfPresentIllness: '',
    systemsReview: null,
    lifeHabits: null,
    socialConditions: null,
    physicalExam: null,
    diagnosticHypothesis: '',
}

// Default systems review structure
const defaultSystemsReview = {
    general: '',
    cardiovascular: '',
    respiratory: '',
    gastrointestinal: '',
    genitourinary: '',
    musculoskeletal: '',
    neurological: '',
    psychiatric: '',
    skin: '',
    endocrine: '',
    hematologic: '',
}

// Default life habits structure
const defaultLifeHabits = {
    smoking: { status: 'never', details: '' },
    alcohol: { status: 'never', details: '' },
    drugs: { status: 'never', details: '' },
    physicalActivity: { status: 'none', details: '' },
    diet: '',
    sleep: '',
}

// Default physical exam structure
const defaultPhysicalExam = {
    generalAppearance: '',
    vitalSigns: '',
    head: '',
    neck: '',
    chest: '',
    abdomen: '',
    extremities: '',
    neurological: '',
    skin: '',
}

export default function AnamneseFormPage() {
    const navigate = useProntuarioNavigate()
    const { clientId, anamneseId } = useProntuarioParams<{ clientId: string; anamneseId: string }>()
    const { user, isProfessional } = useAuth()

    const [formData, setFormData] = useState<FormData>(defaultFormData)
    const [templates, setTemplates] = useState<AnamnesisTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    const isEditing = !!anamneseId
    const basePath = `/person/clients/${clientId}/prontuario`

    useEffect(() => {
        loadData()
    }, [clientId, anamneseId])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            // Load templates
            const companyId = user?.companyId
            if (companyId) {
                const templatesResponse = await getAnamnesisTemplates(companyId)
                if (templatesResponse.success) {
                    setTemplates(templatesResponse.result || [])
                }
            }

            // Load existing anamnesis if editing
            if (anamneseId) {
                const response = await getAnamnesis(parseInt(anamneseId, 10))
                if (response.success && response.result) {
                    const anamnesis = response.result
                    setFormData({
                        professionalId: anamnesis.professionalId?.toString() || '',
                        templateId: anamnesis.templateId || null,
                        consultationId: anamnesis.consultationId || null,
                        occupation: anamnesis.occupation || '',
                        religion: anamnesis.religion || '',
                        informant: anamnesis.informant || '',
                        reliability: anamnesis.reliability || '',
                        chiefComplaint: anamnesis.chiefComplaint || '',
                        historyOfPresentIllness: anamnesis.historyOfPresentIllness || '',
                        systemsReview: anamnesis.systemsReview || defaultSystemsReview,
                        lifeHabits: anamnesis.lifeHabits || defaultLifeHabits,
                        socialConditions: anamnesis.socialConditions || null,
                        physicalExam: anamnesis.physicalExam || defaultPhysicalExam,
                        diagnosticHypothesis: anamnesis.diagnosticHypothesis || '',
                    })
                } else {
                    toast.error(response.error || 'Erro ao carregar anamnese')
                    navigate(`${basePath}/anamneses`)
                }
            } else {
                // Set default structures for new anamnesis
                setFormData(prev => ({
                    ...prev,
                    systemsReview: defaultSystemsReview,
                    lifeHabits: defaultLifeHabits,
                    physicalExam: defaultPhysicalExam,
                }))
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const handleTemplateChange = (templateId: number | null) => {
        setFormData(prev => ({ ...prev, templateId }))
        // Could load template structure here if needed
    }

    const updateField = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const updateSystemsReview = (key: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            systemsReview: { ...prev.systemsReview, [key]: value },
        }))
    }

    const updateLifeHabits = (key: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            lifeHabits: { ...prev.lifeHabits, [key]: value },
        }))
    }

    const updatePhysicalExam = (key: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            physicalExam: { ...prev.physicalExam, [key]: value },
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            if (isEditing && anamneseId) {
                const updateData: UpdateAnamnesisData = {
                    templateId: formData.templateId || undefined,
                    consultationId: formData.consultationId || undefined,
                    occupation: formData.occupation.trim() || undefined,
                    religion: formData.religion.trim() || undefined,
                    informant: formData.informant.trim() || undefined,
                    reliability: formData.reliability.trim() || undefined,
                    chiefComplaint: formData.chiefComplaint.trim() || undefined,
                    historyOfPresentIllness: formData.historyOfPresentIllness.trim() || undefined,
                    systemsReview: formData.systemsReview || undefined,
                    lifeHabits: formData.lifeHabits || undefined,
                    socialConditions: formData.socialConditions || undefined,
                    physicalExam: formData.physicalExam || undefined,
                    diagnosticHypothesis: formData.diagnosticHypothesis.trim() || undefined,
                }

                const response = await updateAnamnesis(parseInt(anamneseId, 10), updateData)
                if (response.success) {
                    toast.success('Anamnese atualizada com sucesso!')
                    navigate(`${basePath}/anamneses`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar anamnese')
                }
            } else {
                const createData: CreateAnamnesisData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    professionalId: isProfessional ? user!.professionalId! : parseInt(formData.professionalId, 10),
                    templateId: formData.templateId || undefined,
                    consultationId: formData.consultationId || undefined,
                    occupation: formData.occupation.trim() || undefined,
                    religion: formData.religion.trim() || undefined,
                    informant: formData.informant.trim() || undefined,
                    reliability: formData.reliability.trim() || undefined,
                    chiefComplaint: formData.chiefComplaint.trim() || undefined,
                    historyOfPresentIllness: formData.historyOfPresentIllness.trim() || undefined,
                    systemsReview: formData.systemsReview || undefined,
                    lifeHabits: formData.lifeHabits || undefined,
                    socialConditions: formData.socialConditions || undefined,
                    physicalExam: formData.physicalExam || undefined,
                    diagnosticHypothesis: formData.diagnosticHypothesis.trim() || undefined,
                }

                const response = await createAnamnesis(createData)
                if (response.success) {
                    toast.success('Anamnese criada com sucesso!')
                    navigate(`${basePath}/anamneses`)
                } else {
                    toast.error(response.error || 'Erro ao criar anamnese')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar anamnese')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`${basePath}/anamneses`)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-secondary/10 rounded-lg">
                            <MdAssignment className="text-2xl text-secondary" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-base-content">
                                {isEditing ? 'Editar Anamnese' : 'Nova Anamnese'}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Preencha os dados da anamnese
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Professional Selection */}
                {!isProfessional && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Profissional Responsável</h3>
                    <div className="form-control w-full max-w-xs">
                        <ProfessionalSearchSelector
                            value={formData.professionalId ? Number(formData.professionalId) : null}
                            onChange={(id) => updateField('professionalId', id ? String(id) : '')}
                            label=""
                            placeholder="Selecione..."
                            disabled={isEditing}
                        />
                    </div>
                </div>
                )}

                {/* Template Selection */}
                {templates.length > 0 && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h3 className="text-lg font-bold text-base-content mb-4">Template</h3>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Selecionar Template</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.templateId || ''}
                                onChange={e => handleTemplateChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                            >
                                <option value="">Sem template</option>
                                {templates.map(template => (
                                    <option key={template.id} value={template.id}>
                                        {template.name} ({MEDICAL_SPECIALTY_LABELS[template.specialty]})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Identification */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Identificação</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Ocupação</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Profissão do paciente"
                                value={formData.occupation}
                                onChange={e => updateField('occupation', e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Religião</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Religião"
                                value={formData.religion}
                                onChange={e => updateField('religion', e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Informante</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Quem forneceu as informações"
                                value={formData.informant}
                                onChange={e => updateField('informant', e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Confiabilidade</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.reliability}
                                onChange={e => updateField('reliability', e.target.value)}
                            >
                                <option value="">Selecione</option>
                                <option value="alta">Alta</option>
                                <option value="media">Média</option>
                                <option value="baixa">Baixa</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Chief Complaint */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Queixa Principal</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered w-full"
                            placeholder="Descreva a queixa principal do paciente nas palavras dele..."
                            rows={3}
                            value={formData.chiefComplaint}
                            onChange={e => updateField('chiefComplaint', e.target.value)}
                        />
                    </div>
                </div>

                {/* History of Present Illness */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">História da Doença Atual (HDA)</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered w-full"
                            placeholder="Descreva a evolução cronológica da queixa principal..."
                            rows={6}
                            value={formData.historyOfPresentIllness}
                            onChange={e => updateField('historyOfPresentIllness', e.target.value)}
                        />
                    </div>
                </div>

                {/* Systems Review */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Revisão de Sistemas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries({
                            general: 'Geral',
                            cardiovascular: 'Cardiovascular',
                            respiratory: 'Respiratório',
                            gastrointestinal: 'Gastrointestinal',
                            genitourinary: 'Geniturinário',
                            musculoskeletal: 'Musculoesquelético',
                            neurological: 'Neurológico',
                            psychiatric: 'Psiquiátrico',
                            skin: 'Pele e Anexos',
                            endocrine: 'Endócrino',
                            hematologic: 'Hematológico',
                        }).map(([key, label]) => (
                            <div key={key} className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">{label}</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full text-sm"
                                    placeholder={`Sintomas ${label.toLowerCase()}...`}
                                    rows={2}
                                    value={formData.systemsReview?.[key] || ''}
                                    onChange={e => updateSystemsReview(key, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Life Habits */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Hábitos de Vida</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tabagismo</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.lifeHabits?.smoking?.status || 'never'}
                                onChange={e => updateLifeHabits('smoking', { ...formData.lifeHabits?.smoking, status: e.target.value })}
                            >
                                <option value="never">Nunca fumou</option>
                                <option value="former">Ex-tabagista</option>
                                <option value="current">Tabagista atual</option>
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Etilismo</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.lifeHabits?.alcohol?.status || 'never'}
                                onChange={e => updateLifeHabits('alcohol', { ...formData.lifeHabits?.alcohol, status: e.target.value })}
                            >
                                <option value="never">Não consome</option>
                                <option value="social">Social</option>
                                <option value="regular">Regular</option>
                                <option value="heavy">Pesado</option>
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Atividade Física</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.lifeHabits?.physicalActivity?.status || 'none'}
                                onChange={e => updateLifeHabits('physicalActivity', { ...formData.lifeHabits?.physicalActivity, status: e.target.value })}
                            >
                                <option value="none">Sedentário</option>
                                <option value="occasional">Ocasional</option>
                                <option value="regular">Regular</option>
                                <option value="intense">Intensa</option>
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Alimentação</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Descreva a alimentação"
                                value={formData.lifeHabits?.diet || ''}
                                onChange={e => updateLifeHabits('diet', e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Sono</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Qualidade e duração do sono"
                                value={formData.lifeHabits?.sleep || ''}
                                onChange={e => updateLifeHabits('sleep', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Physical Exam */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Exame Físico</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries({
                            generalAppearance: 'Ectoscopia',
                            vitalSigns: 'Sinais Vitais',
                            head: 'Cabeça',
                            neck: 'Pescoço',
                            chest: 'Tórax',
                            abdomen: 'Abdome',
                            extremities: 'Extremidades',
                            neurological: 'Neurológico',
                            skin: 'Pele',
                        }).map(([key, label]) => (
                            <div key={key} className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">{label}</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full text-sm"
                                    placeholder={`Achados do exame de ${label.toLowerCase()}...`}
                                    rows={2}
                                    value={formData.physicalExam?.[key] || ''}
                                    onChange={e => updatePhysicalExam(key, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Diagnostic Hypothesis */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Hipótese Diagnóstica</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered w-full"
                            placeholder="Liste as hipóteses diagnósticas..."
                            rows={4}
                            value={formData.diagnosticHypothesis}
                            onChange={e => updateField('diagnosticHypothesis', e.target.value)}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(`${basePath}/anamneses`)}
                        className="btn btn-ghost"
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar Anamnese
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
